import React, { ReactNode } from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface ResponsiveActionButtonProps {
  appearance?: "default" | "alpha";
  label: string;
  icon: ReactNode;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'default';
  disabled?: boolean;
  className?: string;
  title?: string; // Tooltip text, useful when label is hidden
}

const ResponsiveActionButton: React.FC<ResponsiveActionButtonProps> = ({
  appearance = "default",
  label,
  icon,
  onClick,
  variant = 'primary',
  disabled = false,
  className = '',
  title,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const getVariantClasses = () => {
    if (appearance === "alpha") {
      return variant === "primary" ? "bg-v7-ink text-v7-white hover:bg-v7-ink-soft"
        : variant === "danger" ? "border border-v7-rust/30 bg-v7-white text-v7-rust hover:bg-v7-rust/5"
        : "border border-v7-ink/25 bg-v7-white text-v7-ink-soft hover:bg-v7-paper";
    }
    switch (variant) {
      case 'primary':
        return 'bg-blue-600 text-white hover:bg-blue-700';
      case 'secondary':
        return isDarkMode 
          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50';
      case 'danger':
        return 'bg-red-600 text-white hover:bg-red-700';
      case 'default':
      default:
        return isDarkMode
          ? 'text-gray-300 hover:text-white hover:bg-gray-700'
          : 'text-gray-600 hover:text-gray-700 hover:bg-gray-100';
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={title || label}
      className={appearance === "alpha" ? `\n flex items-center justify-center\n transition-colors duration-200\n rounded-v7-control min-h-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark\n p-2 sm:px-4 sm:py-2\n disabled:opacity-50 disabled:cursor-not-allowed\n ${getVariantClasses()}\n ${className}\n ` : `
        flex items-center justify-center
        transition-colors duration-200
        rounded-lg
        p-2 sm:px-4 sm:py-2
        disabled:opacity-50 disabled:cursor-not-allowed
        ${getVariantClasses()}
        ${className}
      `}
    >
      <span className="flex items-center justify-center">
        {/* Clone icon to ensure consistent sizing if needed, or just render */}
        {icon}
      </span>
      <span className="hidden sm:inline-block ml-2 font-medium">
        {label}
      </span>
    </button>
  );
};

export default ResponsiveActionButton;
